import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { ChevronRight, Sparkles, ShieldCheck, Award, Truck } from "lucide-react";

const CATEGORIES = [
  { name: "Rings", slug: "rings", image: "https://images.unsplash.com/photo-1605100804763-247f67b3557e?q=80&w=1200" },
  { name: "Earrings", slug: "earrings", image: "https://images.unsplash.com/photo-1635767798638-3665a0a107cf?q=80&w=1200" },
  { name: "Necklaces", slug: "necklaces", image: "https://images.unsplash.com/photo-1599643477877-530eb83abc8e?q=80&w=1200" },
  { name: "Bangles", slug: "bangles", image: "https://images.unsplash.com/photo-1573408301185-9146fe634ad0?q=80&w=1200" },
  { name: "Bracelets", slug: "bracelets", image: "https://images.unsplash.com/photo-1611955167811-4711904bb9f8?q=80&w=1200" },
  { name: "Bridal", slug: "bridal", image: "https://images.unsplash.com/photo-1600685890506-593fdf55949b?q=80&w=1200" },
];

const HERO = "https://images.unsplash.com/photo-1740431377901-c2f28d50c759?q=80&w=2200";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [bestsellers, setBestsellers] = useState([]);
  const [collections, setCollections] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true, limit: 8 } }).then(r => setFeatured(r.data));
    api.get("/products", { params: { bestseller: true, limit: 4 } }).then(r => setBestsellers(r.data));
    api.get("/collections").then(r => setCollections(r.data));
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[86vh] flex items-center overflow-hidden">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-55"/>
        <div className="absolute inset-0 bg-gradient-to-b from-[#12100E]/70 via-[#12100E]/50 to-[#12100E]"/>
        <div className="absolute inset-0 opacity-30" style={{background: "radial-gradient(ellipse at 65% 40%, rgba(212,175,55,0.35), transparent 50%)"}}/>

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-24 w-full">
          <div className="max-w-2xl reveal">
            <div className="eyebrow flex items-center gap-3"><Sparkles size={14}/> Presented by Lakshya Soni</div>
            <h1 className="font-serif-luxe text-5xl sm:text-6xl lg:text-7xl text-[#FAF8F5] mt-6 leading-[1.05] tracking-tight">
              Jewellery That <br/>Carries an <span className="italic text-[#D4AF37]">Aura.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-[#C2B29F] max-w-lg font-light leading-relaxed">
              Timeless elegance, hand-crafted in Jaipur to become part of your story.
              Every piece is BIS Hallmarked and certified for a lifetime.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link data-testid="cta-shop-jewellery" to="/shop" className="btn-gold">Shop Jewellery</Link>
              <Link data-testid="cta-explore-collection" to="/collections" className="btn-outline-gold">Explore Collection</Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[10px] tracking-[0.4em] uppercase text-[#C2B29F] animate-pulse">Scroll to Discover</div>
      </section>

      {/* TRUST BAR */}
      <section className="border-y border-[#D4AF37]/15 bg-[#0f0c0a]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-4 gap-8 py-8">
          {[
            {i: ShieldCheck, t: "BIS Hallmarked", s: "916 & 750 gold, certified"},
            {i: Award, t: "IGI / GIA Certified", s: "Every diamond authenticated"},
            {i: Truck, t: "Insured Delivery", s: "Complimentary, PAN India"},
            {i: Sparkles, t: "Lifetime Care", s: "Free polish & re-plating"},
          ].map((x, i) => (
            <div key={i} className="flex items-center gap-3 justify-center md:justify-start">
              <x.i size={22} className="text-[#D4AF37]"/>
              <div>
                <div className="text-[13px] tracking-[0.12em] uppercase text-[#FAF8F5]">{x.t}</div>
                <div className="text-xs text-[#C2B29F]">{x.s}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* SHOP BY CATEGORY */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="eyebrow">Shop the Boutique</div>
            <h2 className="font-serif-luxe text-4xl sm:text-5xl text-[#FAF8F5] mt-3">Browse by Category</h2>
          </div>
          <Link to="/shop" className="hidden sm:flex items-center gap-1 text-[11px] tracking-[0.24em] uppercase text-[#D4AF37] gold-underline">View All <ChevronRight size={14}/></Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
          {CATEGORIES.map((c, i) => (
            <Link
              key={c.slug}
              to={`/shop/${c.slug}`}
              data-testid={`category-${c.slug}`}
              className="group relative aspect-[4/5] md:aspect-[3/4] overflow-hidden bg-[#221712] reveal"
              style={{animationDelay: `${i * 80}ms`}}
            >
              <img src={c.image} alt={c.name} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-[1200ms]"/>
              <div className="absolute inset-0 bg-gradient-to-t from-[#12100E] via-[#12100E]/40 to-transparent"/>
              <div className="absolute inset-x-0 bottom-0 p-6">
                <div className="font-serif-luxe text-2xl sm:text-3xl text-[#FAF8F5]">{c.name}</div>
                <div className="mt-2 flex items-center gap-1 text-[11px] tracking-[0.24em] uppercase text-[#D4AF37]">Explore <ChevronRight size={12}/></div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <div className="text-center mb-14">
          <div className="eyebrow">The Curation</div>
          <h2 className="font-serif-luxe text-4xl sm:text-5xl text-[#FAF8F5] mt-3">Featured Masterpieces</h2>
          <p className="text-[#C2B29F] mt-4 max-w-xl mx-auto text-sm">A hand-picked edit of the season's most coveted pieces from our master karigars.</p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
          {featured.map((p, i) => <ProductCard key={p.id} product={p} index={i}/>)}
        </div>
      </section>

      {/* BRIDAL BANNER */}
      <section className="my-24 relative min-h-[60vh] flex items-center overflow-hidden">
        <img src="https://images.unsplash.com/photo-1600685890506-593fdf55949b?q=80&w=2000" alt="" className="absolute inset-0 w-full h-full object-cover opacity-50"/>
        <div className="absolute inset-0 bg-gradient-to-r from-[#12100E]/85 via-[#12100E]/60 to-transparent"/>
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 w-full py-24">
          <div className="max-w-lg">
            <div className="eyebrow">The Bridal Aura</div>
            <h2 className="font-serif-luxe text-4xl sm:text-5xl text-[#FAF8F5] mt-4">For the Bride Who Carries an Aura.</h2>
            <p className="mt-5 text-[#C2B29F] text-sm sm:text-base leading-relaxed">
              Ceremonial trousseau sets, hand-set with polki, kundan and uncut diamonds. Crafted over 90 days
              by 12 karigars — a bridal wardrobe made to be inherited.
            </p>
            <Link to="/shop/bridal" className="btn-gold mt-8">Discover Bridal</Link>
          </div>
        </div>
      </section>

      {/* COLLECTIONS */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="eyebrow">Curated Stories</div>
            <h2 className="font-serif-luxe text-4xl sm:text-5xl text-[#FAF8F5] mt-3">The Collections</h2>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
          {collections.slice(0, 4).map((c, i) => (
            <Link key={c.slug} to={`/collections/${c.slug}`} data-testid={`collection-${c.slug}`} className="group relative aspect-[16/10] overflow-hidden bg-[#221712] reveal" style={{animationDelay: `${i * 90}ms`}}>
              <img src={c.image} alt={c.name} className="w-full h-full object-cover opacity-75 group-hover:opacity-100 group-hover:scale-105 transition-all duration-[1200ms]"/>
              <div className="absolute inset-0 bg-gradient-to-t from-[#12100E] via-[#12100E]/40 to-transparent"/>
              <div className="absolute inset-x-0 bottom-0 p-8">
                <div className="eyebrow text-[#D4AF37]">{c.tagline}</div>
                <div className="font-serif-luxe text-3xl text-[#FAF8F5] mt-2">{c.name}</div>
                <div className="mt-3 text-sm text-[#C2B29F] max-w-md">{c.description}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* BESTSELLERS */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <div className="text-center mb-14">
          <div className="eyebrow">Loved by our Clients</div>
          <h2 className="font-serif-luxe text-4xl sm:text-5xl text-[#FAF8F5] mt-3">Bestsellers</h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
          {bestsellers.map((p, i) => <ProductCard key={p.id} product={p} index={i}/>)}
        </div>
      </section>

      {/* FOUNDER STORY */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="relative aspect-[4/5] overflow-hidden bg-[#221712]">
            <img src="https://images.unsplash.com/photo-1617633150878-7df1d12a9a57?q=80&w=1400" alt="Lakshya Soni" className="w-full h-full object-cover"/>
            <div className="absolute inset-0 bg-gradient-to-t from-[#12100E]/60 to-transparent"/>
          </div>
          <div>
            <div className="eyebrow">The Atelier</div>
            <h2 className="font-serif-luxe text-4xl sm:text-5xl text-[#FAF8F5] mt-3 leading-tight">Every piece begins with a prayer, and ends with an aura.</h2>
            <p className="mt-6 text-[#C2B29F] text-sm sm:text-base leading-relaxed">
              Laxmi Aura Jewels was founded by <em className="text-[#D4AF37] font-serif-luxe">Lakshya Soni</em> — a fourth-generation jeweller from Jaipur — with a simple belief:
              that jewellery is not decoration, it is inheritance. Every stone is hand-set, every motif drawn from
              the temples and palaces of Rajasthan, and every gram of gold hallmarked for the woman who will wear it
              — and the daughters who will inherit it.
            </p>
            <Link to="/about" className="btn-outline-gold mt-8">Read Our Story</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
